// Brand chart palette + theme. The LLM references colours by NAME (e.g. "brandRed"),
// never by hex — these names are documented in the analytics agent's instructions and
// resolved here. Multi-series charts auto-assign from CATEGORICAL when no colour is given.

export const CHART_COLORS: Record<string, string> = {
  brandRed: '#cf2330',
  charcoal: '#121212',
  slate: '#6e6e80',
  sage: '#7c8a72',
  wheat: '#e3b23c',
  sky: '#4a7a96',
  plum: '#7d5a7a',
  cream: '#f5f5ed',
}

// Order used to auto-assign colours across series/slices.
export const CATEGORICAL: string[] = [
  CHART_COLORS.brandRed,
  CHART_COLORS.sage,
  CHART_COLORS.wheat,
  CHART_COLORS.sky,
  CHART_COLORS.plum,
  CHART_COLORS.slate,
  CHART_COLORS.charcoal,
]

// Semantic aliases the model may use for meaning rather than identity.
const SEMANTIC: Record<string, string> = {
  positive: CHART_COLORS.sage,
  negative: CHART_COLORS.brandRed,
  neutral: CHART_COLORS.slate,
}

/** Resolve a colour name to a hex value, falling back to the categorical sequence. */
export const resolveColor = (
  name: string | undefined,
  index: number
): string => {
  if (name) {
    if (CHART_COLORS[name]) return CHART_COLORS[name]
    if (SEMANTIC[name]) return SEMANTIC[name]
    if (/^#[0-9a-fA-F]{3,8}$/.test(name)) return name // tolerate a raw hex
  }
  return CATEGORICAL[index % CATEGORICAL.length]
}

export type ValueFormat = 'currencyGBP' | 'number' | 'percent' | 'plain'

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 2,
})
const gbpCompact = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  notation: 'compact',
  maximumFractionDigits: 1,
})
const num = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 })
const numCompact = new Intl.NumberFormat('en-GB', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

/** Format a numeric value for axes, labels and tooltips. `compact` => £165k / 1.2k. */
export const formatValue = (
  value: number | string | null | undefined,
  format: ValueFormat = 'plain',
  compact = false
): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return String(value)
  switch (format) {
    case 'currencyGBP':
      return (compact ? gbpCompact : gbp).format(n)
    case 'percent':
      return `${(compact ? numCompact : num).format(n)}%`
    case 'number':
      return (compact ? numCompact : num).format(n)
    default:
      return String(value)
  }
}

// Nivo theme matching the chat UI (warm neutrals, brand-friendly).
export const nivoTheme = {
  background: 'transparent',
  fontFamily:
    "'Basis Grotesque Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  fontSize: 12,
  text: { fill: '#121212' },
  axis: {
    domain: { line: { stroke: '#e5e5e7', strokeWidth: 1 } },
    ticks: {
      line: { stroke: '#e5e5e7', strokeWidth: 1 },
      text: { fill: '#6e6e80', fontSize: 11 },
    },
    legend: { text: { fill: '#6e6e80', fontSize: 12, fontWeight: 600 } },
  },
  grid: { line: { stroke: '#eeeee5', strokeWidth: 1 } },
  legends: { text: { fill: '#6e6e80', fontSize: 12 } },
  tooltip: {
    container: {
      background: '#ffffff',
      color: '#121212',
      fontSize: 12,
      borderRadius: 8,
      border: '1px solid #e5e5e7',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
  },
}
